import { formatISO9075 } from 'date-fns'
import { useState } from 'react'
import {
    ContentView,
    EnrichedContent,
    ProfileViewWithShadowBan,
    Screenshot
} from '../../../../api/client'
import { isAd } from '../../../../util/ads'
import { getModerationStyles } from '../../../../util/moderationStyles'
import { getInLocalTime } from '../../../../util/timeHelpers'
import ShadowBannedBadge from '../../../badges/ShadowBannedBadge'
import { RelativeDateDisplay, StandardDateDisplay } from '../../../date/StandardDateDisplay'
import ContentMoreActions from '../ContentMoreActions'
import ContentMetadataModal from './ContentMetadataModal'
import AdBadge from '../AdBadge'
import ReplyBadge from '../ReplyBadge'

type Props = {
    content: ContentView
    enrichedContent: EnrichedContent | undefined
    profileCache?: ProfileViewWithShadowBan[]
    onShadowBanChange?: (contentId: number, profileId: number) => void
    newProfileIds?: Map<number, number>
    screenshot?: Screenshot | null
    onScreenshotRefetch?: () => void
    // Both optional: only ContentCard supplies them, to draw the platform icon inline when the rail is hidden.
    iconName?: string
    moderationStyle?: string
}

export default function ContentMetadata(props: Props) {
    const [open, setOpen] = useState(false)
    const {
        content,
        enrichedContent,
        profileCache,
        onShadowBanChange,
        newProfileIds,
        screenshot,
        onScreenshotRefetch,
        iconName,
        moderationStyle
    } = props
    const showAdLabel = isAd(content)

    // Check if the author is shadow banned
    const profileMap = new Map<number, ProfileViewWithShadowBan>(
        (profileCache || []).map((profile) => [profile.id, profile])
    )
    // Use the newProfileIds map to find the profile ID if the content doesn't have one yet - it won't when the profile has just been created by assigning shadow banning.
    const effectiveProfileId = content.authorProfileId || newProfileIds?.get(content.id) || -1
    const authorProfile = profileMap.get(effectiveProfileId)
    const isShadowBanned = authorProfile?.shadowBanStatusCode === 1

    /**
     * The name we want to display might be a handle, e.g. '@jamescolepotter' or
     * it might be a name, e.g. James Cole Pottery. This depends on the service.
     */
    // Mirrors the rail: only the safelisted moderation colours resolve to a class, so
    // anything else (an unknown or missing value) falls back to the rail's grey.
    const iconColourClass = getModerationStyles(moderationStyle).indicator
    const hasHandle = !!content.serviceAuthorDisplayUsername
    const absoluteDate = content.datePosted ? formatISO9075(getInLocalTime(content.datePosted)) : ''

    return (
        // ONE truncating line. Shrink order across the card's metadata row: the account
        // name (shrink factor 100) gives way first, then this author name and handle,
        // which truncate last. Date, menu, Ad, Reply, score and links never shrink, so nothing
        // wraps word by word as the date did on a 344px reply card. `mr-auto` pushes the
        // score, account and links (siblings of this element) to the end of the row.
        // flex-initial keeps the content width while there is room; min-w-0 lets the
        // row go below it.
        <div className="mr-auto flex min-w-0 flex-initial flex-row flex-nowrap items-center">
            {iconName && (
                // Stands in for the green platform rail, which is hidden on narrow cards.
                // The rail's moderation colour carries over as the icon's text colour.
                <i
                    className={`fab ${iconName} mr-1.5 shrink-0 text-base @sm:hidden ${
                        iconColourClass
                    }`}
                    data-tooltip-id="tooltip-id"
                    data-tooltip-content={`This content comes from ${content.socialMediaServiceName}`}
                ></i>
            )}
            {content.serviceAuthorName && (
                // Handle wins over display name below the wide tier when both exist.
                <div
                    className={`min-w-0 truncate pr-2 font-medium ${
                        hasHandle ? 'hidden @lg:block' : ''
                    }`}
                >
                    {content.serviceAuthorName}
                </div>
            )}
            {content.serviceAuthorDisplayUsername && content.socialMediaAuthorLink && (
                <div className="min-w-0 truncate pr-1 text-arwen-700 hover:text-arwen-800">
                    <a href={content.socialMediaAuthorLink || ''} target="_blank" rel="noreferrer">
                        {content.serviceAuthorDisplayUsername}
                    </a>
                </div>
            )}
            {content.serviceAuthorDisplayUsername && !!!content.socialMediaAuthorLink && (
                <div className="min-w-0 truncate pr-1 text-ink-700">
                    {content.serviceAuthorDisplayUsername}
                </div>
            )}
            {isShadowBanned && (
                // The badge has no icon form, so it waits for @5xl (1024px) where there is
                // room for ~115px of fixed width; the menu still offers "Remove shadow ban"
                // at every width.
                <div className="hidden shrink-0 @5xl:block">
                    <ShadowBannedBadge className="mx-2 mb-1" />
                </div>
            )}
            <div className="shrink-0 pr-1 text-ink-700">·</div>
            <div
                className="shrink-0 whitespace-nowrap pr-1 text-ink-700"
                data-cy-date={content.datePosted}
                title={absoluteDate}
            >
                <RelativeDateDisplay date={content.datePosted} short />
            </div>
            <div className="hidden shrink-0 whitespace-nowrap pr-1 text-ink-600 @5xl:block">
                ( <StandardDateDisplay date={content.datePosted} /> )
            </div>
            <div className="flex shrink-0 flex-col items-center justify-center space-y-4">
                <div className="flex">
                    <ContentMoreActions
                        content={content}
                        enrichedContent={enrichedContent}
                        onViewDetails={() => setOpen(true)}
                        isShadowBanned={isShadowBanned}
                        onShadowBanChange={onShadowBanChange}
                        screenshot={screenshot}
                        onScreenshotRefetch={onScreenshotRefetch}
                    />
                </div>
            </div>

            <ContentMetadataModal
                open={open}
                setOpen={setOpen}
                content={content}
                enrichedContent={enrichedContent}
            />
            {showAdLabel ? <AdBadge className="mx-1 @lg:mx-2 @lg:mb-1" /> : undefined}
            <ReplyBadge content={content} className="mx-1 @lg:mx-2 @lg:mb-1" />
        </div>
    )
}
